import * as FileSystem from 'expo-file-system/legacy';
import * as MediaLibrary from 'expo-media-library';
import mime from 'mime';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, Platform } from 'react-native';

import { Alert } from '@components/Alert';
import { saveToLegacyAndroidDownloads } from '@utils/androidLegacyDownloadDir';
import { logError } from '@utils/logNonFatalError';

// react-native-blob-util is native-only. This hook is imported by the web
// download path (via useDownloadFiles), so a top-level import would pull a
// native module into the web bundle even though the branch that uses it
// never runs there (Copilot review, PR #5298) -- guard it the same way
// useChatUploadManager.tsx guards react-native-fs.
let RNBlobUtil: typeof import('react-native-blob-util').default;
if (Platform.OS !== 'web') {
  RNBlobUtil = require('react-native-blob-util').default;
}

export const useSaveImage = () => {
  const { t } = useTranslation('models');

  const [loading, setLoading] = useState(false);

  const saveImage = async (
    uri: string,
    name: string,
    showMsg?: boolean,
    contentType?: string
  ) => {
    showMsg ??= true;
    setLoading(true);
    let extension = '';
    if (!name.includes('.')) {
      if (
        contentType?.includes('video') ||
        name.includes('video') ||
        uri.includes('video')
      ) {
        extension = '.mp4';
      } else {
        extension = '.jpg';
      }
    }
    const fileUri = FileSystem.documentDirectory + name + extension;
    try {
      const { uri: downloadedUri } = await FileSystem.downloadAsync(
        uri,
        fileUri
      );
      return await save(downloadedUri, showMsg, contentType);
    } catch (error) {
      console.error('Error downloading file:', error);
      if (showMsg) {
        Alert.alert(
          t('shared:error'),
          t('models:downloadError', {
            defaultValue: 'Failed to download file. Please try again.',
          }),
          [{ text: t('shared:ok') }]
        );
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const save = async (
    uri: string,
    _showMsg?: boolean,
    contentType?: string
  ) => {
    if (Platform.OS === 'android') {
      // TA-9921: route through the MediaStore Downloads collection on API
      // 29+ (scoped storage) rather than expo-media-library's
      // saveToLibraryAsync / Asset.create -- neither of those targets
      // Downloads, they save to the general Photos library, which is the
      // bug this ticket exists for.
      //
      // WRITE_EXTERNAL_STORAGE / READ_EXTERNAL_STORAGE are permanently
      // blocked from the manifest (app.config.js blockedPermissions,
      // TA-10087 -- Google Play rejected a build over broad media
      // permissions), so there is no legacy-permission fallback available
      // below API 29; MediaCollection.copyToMediaStore itself is API 29+
      // only. Below that, route through Storage Access Framework instead
      // (see androidLegacyDownloadDir.ts for why SAF is scoped to <29 only).
      // decodeURI first: uri can carry percent escapes (e.g. a space as
      // %20), and the basename is used as the user-visible MediaStore/SAF
      // file name, not just a path segment -- leaving it encoded registers
      // the download as e.g. "My%20Photo.jpg" (Copilot review, PR #5298).
      const fileName = decodeURI(uri).split('/').pop() ?? 'image.jpg';
      // A falsy (not just missing) contentType -- e.g. an empty string from
      // the backend -- must also fall through to inferring from the
      // filename, so use || rather than ?? (Copilot review, PR #5298).
      const mimeType =
        contentType || mime.getType(fileName) || 'application/octet-stream';

      if (Number(Platform.Version) < 29) {
        const saved = await saveToLegacyAndroidDownloads(
          uri,
          fileName,
          mimeType
        );
        if (!saved && _showMsg) {
          Alert.alert(
            t('models:accessDenied.media.title'),
            t('models:accessDenied.media.message'),
            [
              { text: t('shared:cancel') },
              {
                text: t('shared:settings'),
                onPress: () => Linking.openSettings(),
              },
            ]
          );
        }
        return saved;
      }

      try {
        await RNBlobUtil.MediaCollection.copyToMediaStore(
          { name: fileName, parentFolder: '', mimeType },
          'Download',
          // The downloaded uri can carry percent escapes (e.g. a space as
          // %20); decode before handing RNBlobUtil a native path, or
          // filenames with spaces or non-ASCII characters fail to copy
          // (Copilot review, PR #5298).
          decodeURI(uri.replace('file://', ''))
        );
        return true;
      } catch (error) {
        logError({
          message: 'Save media error',
          errorMessage: error instanceof Error ? error.message : String(error),
          operationName: 'copyToMediaStore',
        });
        if (_showMsg) {
          Alert.alert(
            t('models:accessDenied.media.title'),
            t('models:accessDenied.media.message'),
            [
              { text: t('shared:cancel') },
              {
                text: t('shared:settings'),
                onPress: () => Linking.openSettings(),
              },
            ]
          );
        }
        return false;
      }
    }

    const { status } = await MediaLibrary.requestPermissionsAsync();

    if (status === 'granted') {
      const asset = await MediaLibrary.createAssetAsync(uri);
      const taskTagAlbum = await MediaLibrary.getAlbumAsync('TaskTag');
      if (!taskTagAlbum) {
        await MediaLibrary.createAlbumAsync('TaskTag', asset, false);
      } else {
        await MediaLibrary.addAssetsToAlbumAsync(asset, taskTagAlbum);
      }
      return true;
    } else {
      setLoading(false);
      Alert.alert(
        t('models:accessDenied.media.title'),
        t('models:accessDenied.media.message'),
        [
          { text: t('shared:cancel') },
          {
            text: t('shared:settings'),
            onPress: () => Linking.openSettings(),
          },
        ]
      );
      return false;
    }
  };

  return {
    saveImage,
    loading,
  };
};
